/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Fetching } from "components/common/loading/fetching";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useMaterials } from "hooks/useMaterials";
import { useMemo, useState } from "react";
import { useGetStrategyDashboardCrewComparisonQuery } from "src/store/api/generatedApi";
import { MaterialsToggle } from "../common/MaterialsToggle";
import { MonthStepper } from "../common/MonthStepper";
import { StrikeRulesBox } from "../common/StrikeRulesBox";
import { CrewLeaderboard, SkeletonCrewLeaderboard } from "./CrewLeaderboard";
import { HeatStatusesChart } from "./HeatStatusesChart";
import { MaterialsChart } from "./MaterialsChart";
import { sorted } from "helpers";
import { parseLocalDateTime } from "src/utils/parseLocalDateTime";
import {
  getCurrentCalendarMonth,
  toLocalCalendarTime,
} from "src/utils/toLocalCalendarTime";

export const CrewComparisonTab = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { localTimezone } = useLocalDatetime();
  const { materials } = useMaterials();
  const [ignoredMaterials, setIgnoredMaterials] = useState(
    () => new Set<string>()
  );
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(() =>
    getCurrentCalendarMonth(localTimezone)
  );

  const { start, end } = useMemo(() => {
    const calendarNow = toLocalCalendarTime(dayjs(), localTimezone);
    const currentMonth = calendarNow.startOf("month");
    const isCurrentMonth = selectedMonth.isSame(currentMonth, "month");

    const startOfMonth = selectedMonth.startOf("month");
    const endOfMonth = isCurrentMonth
      ? calendarNow.endOf("day")
      : selectedMonth.endOf("month");

    return {
      start: parseLocalDateTime(startOfMonth, localTimezone).toISOString(),
      end: parseLocalDateTime(endOfMonth, localTimezone).toISOString(),
    };
  }, [selectedMonth, localTimezone]);

  const { data, error, isFetching } =
    useGetStrategyDashboardCrewComparisonQuery({
      start,
      end,
      tenantName,
    });

  const ignorableMaterialSets = useMemo(
    () => data?.crew_data.map(({ ignored_materials }) => ignored_materials),
    [data]
  );

  const materialOrdering = useMemo(
    () => sorted(materials ?? [], (a) => a.ordering).map((m) => m.name),
    [materials]
  );

  const dataWithExclusions = useMemo(() => {
    const withoutMaterials = data?.crew_data.find(({ ignored_materials }) => {
      return [...ignoredMaterials].every((m) => ignored_materials.includes(m));
    });
    if (!withoutMaterials) return withoutMaterials;
    const crews = [...withoutMaterials.crews];
    // Put "All" last
    crews.sort((a, b) => {
      if (a.crew_id === "All") return -1;
      if (b.crew_id === "All") return 1;
      return a.crew_id.localeCompare(b.crew_id);
    });
    return {
      ...withoutMaterials,
      crews,
    };
  }, [data, ignoredMaterials]);

  return (
    <Stack spacing={2} sx={{ p: 2, maxWidth: 1400 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="stretch"
        flex={1}
        width="100%"
        spacing={2}
      >
        <Stack direction="column" alignItems="stretch" gap={1}>
          <Stack mx={-4}>
            <MonthStepper
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
            />
          </Stack>

          {ignorableMaterialSets !== undefined &&
          ignorableMaterialSets.length > 0 ? (
            <MaterialsToggle
              ignorableMaterialSets={ignorableMaterialSets}
              ignoredMaterials={ignoredMaterials}
              setIgnoredMaterials={setIgnoredMaterials}
              materialOrdering={materialOrdering}
            />
          ) : null}
        </Stack>
        {data?.strike_rules !== undefined && data?.strike_rules?.length > 0 && (
          <StrikeRulesBox strikeRules={data?.strike_rules} />
        )}
        <Stack direction="column" alignItems="stretch">
          {isFetching || error !== undefined ? (
            <SkeletonCrewLeaderboard />
          ) : (
            dataWithExclusions && (
              <CrewLeaderboard
                key={dataWithExclusions.date_range_start}
                data={dataWithExclusions}
              />
            )
          )}
        </Stack>
      </Stack>

      {isFetching ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{t("errorLoadingData")}</Alert>
      ) : !dataWithExclusions ? (
        <Alert severity="info">{t("noData")}</Alert>
      ) : (
        <Fetching fetching={isFetching}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="h6" gutterBottom>
              {t("materialDistribution")}
            </Typography>
            <MaterialsChart data={dataWithExclusions} />
          </Paper>

          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="h6" gutterBottom>
              {t("heatStatuses")}
            </Typography>
            <HeatStatusesChart data={dataWithExclusions} />
          </Paper>
        </Fetching>
      )}
    </Stack>
  );
};
